import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PlacowkaService from '@/services/PlacowkaService'

const emptyPlacowkaDetails = {
	nazwa: '',
	adres: '',
	miasto: '',
	wojewodztwo: '',
	typ: '',
	nip: '',
}

const normalizePlacowkaDetails = data => ({
	nazwa: data?.nazwa || '',
	adres: data?.adres || '',
	miasto: data?.id_miasto?.nazwa || data?.miasto || '',
	wojewodztwo: data?.id_wojewodztwo?.nazwa || data?.wojewodztwo || '',
	typ: data?.id_typ?.typ || data?.typ || '',
	nip: data?.nip || '',
})

export default function PlacowkaCard({ placowkaId, placowkaData, handleChange, onLoaded }) {
	const { t } = useTranslation()
	const [placowkaDetails, setPlacowkaDetails] = useState(emptyPlacowkaDetails)
	const [loading, setLoading] = useState(false)
	const [loadError, setLoadError] = useState(false)

	const loadPlacowka = useCallback(async id => {
		if (!id) return
		setLoading(true)
		setLoadError(false)

		const { data, error } = await PlacowkaService.getById(id)
		if (error) {
			setLoadError(true)
			setPlacowkaDetails(emptyPlacowkaDetails)
			setLoading(false)
			return
		}

		if (data) {
			setPlacowkaDetails(normalizePlacowkaDetails(data))
			if (onLoaded) {
				onLoaded(data)
			}
		}

		setLoading(false)
	}, [onLoaded])

	useEffect(() => {
		if (!placowkaId) {
			if (placowkaData) {
				setPlacowkaDetails(normalizePlacowkaDetails(placowkaData))
			} else {
				setPlacowkaDetails(emptyPlacowkaDetails)
			}
			return
		}
		loadPlacowka(placowkaId)
	}, [loadPlacowka, placowkaData, placowkaId])

	const getDisplayValue = value => {
		if (loading) return t('placowkaCard.loading')
		if (loadError) return t('placowkaCard.errorLoading')
		return value || t('placowkaCard.na')
	}

	return (
		<div className="rounded-none sm:rounded-lg border border-gray-200 p-4">
			<div className="w-full">
				<div className="flex justify-between items-center mb-2">
					<div className="text-sm text-gray-600">{t('placowkaCard.title')}</div>
					<button
						type="button"
						onClick={handleChange}
						className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded border border-blue-200 hover:border-blue-300 transition-colors"
					>
						{t('placowkaCard.changePlacowka')}
					</button>
				</div>

				<div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:grid-cols-6">
					<div>
						<div className="text-gray-600">{t('placowkaCard.labels.name')}</div>
						<div className="font-medium text-gray-900">{getDisplayValue(placowkaDetails.nazwa)}</div>
					</div>

					<div className="min-w-0">
						<div className="text-gray-600">{t('placowkaCard.labels.address')}</div>
						<div className="font-medium text-gray-900 break-words">{getDisplayValue(placowkaDetails.adres)}</div>
					</div>

					<div>
						<div className="text-gray-600">{t('placowkaCard.labels.city')}</div>
						<div className="font-medium text-gray-900">{getDisplayValue(placowkaDetails.miasto)}</div>
					</div>

					<div>
						<div className="text-gray-600">{t('placowkaCard.labels.voivodeship')}</div>
						<div className="font-medium text-gray-900">{getDisplayValue(placowkaDetails.wojewodztwo)}</div>
					</div>

					<div>
						<div className="text-gray-600">{t('placowkaCard.labels.type')}</div>
						<div className="font-medium text-gray-900">{getDisplayValue(placowkaDetails.typ)}</div>
					</div>

					<div>
						<div className="text-gray-600">{t('placowkaCard.labels.nip')}</div>
						<div className="font-medium text-gray-900">{getDisplayValue(placowkaDetails.nip)}</div>
					</div>
				</div>
			</div>
		</div>
	)
}
